import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { combineLatest, EMPTY, from, Observable } from 'rxjs';
import { concatMap, first } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService, UserOption } from '../../projects/projects.service';
import { formatDateStamp } from '../../shared/date.utils';
import { TaskParentContainerType, TaskStatus, TaskViewer } from '../../shared/enums/deprecated-task.enum';
import { Task, TaskInput } from '../../shared/models/deprecated-task.model';
import { ReadTaskProtocol } from '../../shared/models/read-task-protocol.model';
import { User } from '../../shared/models/user.model';
import { DuplicateStrategy } from '../create-task-dialog/create-task-dialog.component';
import { TasksService } from '../tasks.service';
import { getHierarchyDisplay, getTaskParentLabels } from '../tasks.utils';

interface DuplicateTaskRow {
  task: {
    _id: string;
    protocolId: string;
    protocolLabel: string;
    container: {
      label: string;
      hierarchy: string;
    };
    assignee: string;
    duplicateId: string; // human readable task id
    parent: {
      id: string;
      type: TaskParentContainerType;
    };
    viewer: TaskViewer;
  };
  editing: {
    assignee: boolean;
    dueDate: boolean;
  };
  form: FormGroup;
}

export interface TaskDuplicatesResult {
  duplicatesCreated: number;
  strategy: DuplicateStrategy;
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'task-duplicates-dialog',
  },
  selector: 'task-duplicates-dialog',
  templateUrl: './task-duplicates-dialog.component.html',
  styleUrls: ['./task-duplicates-dialog.component.scss'],
  standalone: false,
})
export class TaskDuplicatesDialogComponent {
  data = inject<{
    dueDate: Date;
    duplicates: Task[];
    tags: string[];
  }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<TaskDuplicatesDialogComponent, TaskDuplicatesResult>>(MatDialogRef);
  private coreApi = inject(CoreApiService);
  private projectsService = inject(ProjectsService);
  private notifications = inject(NotificationService);
  private logger = inject(LoggingService);
  private tasksService = inject(TasksService);

  defaultDueDate = this.data.dueDate ? formatDateStamp(this.data.dueDate) : '';
  duplicateColumns = ['select', 'protocol', 'container', 'assignee', 'dueDate', 'taskId'];
  duplicates = new MatTableDataSource<DuplicateTaskRow>();
  labels: Record<string, string>;
  readProtocols: Record<string, ReadTaskProtocol> = {};
  selectedTaskCount = 0;
  selectedTasks: Record<string, boolean> = {};
  selectionValid = true;
  userMap: Record<string, User> = {};
  userOptions$: Observable<UserOption[]> = EMPTY;

  constructor() {
    const data = this.data;

    this.userOptions$ = this.projectsService.userOptions$;
    combineLatest([this.tasksService.getReadProtocols(), this.projectsService.getUsers()])
      .pipe(first())
      .subscribe(([readProtocols, users]) => {
        this.labels = {
          acquisition: 'Acquisition',
          assigneeRequired: 'Assignee is required',
          file: 'File',
          group: 'Group',
          project: 'Project',
          session: 'Session',
          subject: 'Subject',
        };
        for (const proto of readProtocols) {
          this.readProtocols[proto._id] = proto;
        }
        for (const user of users) {
          this.userMap[user._id] = user;
        }
        this.duplicates.data = this.buildTableRows(data.duplicates);
      });
  }

  buildTableRows(tasks: Task[]): DuplicateTaskRow[] {
    return tasks.map((existingTask) => {
      const parentLabels = getTaskParentLabels(existingTask);
      if (this.selectedTasks[existingTask._id] === undefined) {
        this.selectedTasks[existingTask._id] = false;
      }

      const form = new FormGroup({
        assignee: new FormControl(existingTask.assignee, [Validators.required]),
        dueDate: new FormControl(this.data.dueDate, [this.futureDateValidator]),
      });
      form.valueChanges.subscribe(() => this.validateTable());

      return {
        task: {
          _id: existingTask._id,
          protocolId: existingTask.protocol_id,
          protocolLabel: this.readProtocols[existingTask.protocol_id] ? this.readProtocols[existingTask.protocol_id].label : '',
          container: {
            label: parentLabels[existingTask.parent.type],
            hierarchy: getHierarchyDisplay(this.labels, parentLabels),
          },
          user: this.userMap[existingTask.assignee],
          duplicateId: existingTask.task_id,
          assignee: existingTask.assignee,
          parent: existingTask.parent,
          viewer: existingTask.viewer,
        },
        editing: {
          assignee: false,
          dueDate: false,
        },
        form,
      };
    });
  }

  toggleSelectAll(checked: boolean): void {
    for (const taskId of Object.keys(this.selectedTasks)) {
      this.selectedTasks[taskId] = checked;
    }
    this.validateTable();
  }

  validateTable(): void {
    const selectedTaskIds = Object.entries(this.selectedTasks)
      .filter(([, selected]) => selected)
      .map(([taskId]) => taskId);
    const tasks = this.duplicates.data.filter(({ task }) => selectedTaskIds.includes(task._id));
    this.selectedTaskCount = selectedTaskIds.length;
    this.selectionValid = tasks.every(({ form }) => form.valid);
  }

  createTasks(includeDuplicates: boolean): void {
    if (this.selectedTaskCount > 0 && includeDuplicates) {
      const taskIds = Object.entries(this.selectedTasks)
        .filter(([, selected]) => selected)
        .map(([taskId]) => taskId);
      const tasks = this.duplicates.data.filter((row) => taskIds.includes(row.task._id));
      from(tasks)
        .pipe(
          concatMap(({ task, form }) => {
            const duplicateTask: TaskInput = {
              assignee: form.get('assignee').value,
              due_date: form.get('dueDate').value ? form.get('dueDate').value.toISOString() : null,
              parent: task.parent,
              protocol_id: task.protocolId,
              status: TaskStatus.Todo,
              tags: this.data.tags,
              viewer: task.viewer,
            };
            return this.coreApi.readerTasks.post(duplicateTask);
          }),
        )
        .subscribe({
          error: (error) => {
            this.logger.error(error);
            this.notifications.error('tasks.bulkCreate.error');
          },
          complete: () => {
            this.dialogRef.close({ duplicatesCreated: tasks.length, strategy: DuplicateStrategy.CreateAll });
          },
        });
      return;
    }
    return this.dialogRef.close({ duplicatesCreated: 0, strategy: DuplicateStrategy.IgnoreDuplicates });
  }

  futureDateValidator: ValidatorFn = (control) => {
    if (!control.value) {
      return null;
    }
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return control.value < now ? { dateIsPast: true } : null;
  };

  requiredAssigneeValidator: ValidatorFn = (control) => {
    return !control.value ? { required: this.labels.assigneeRequired } : null;
  };
}
